import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';

const PROFESSIONS = [
  { name: 'Architect',              credit: 'LU',  example: '36 LUs / 3 years (NY)',   link: '/profession/architect' },
  { name: 'Professional Engineer',  credit: 'PDH', example: '30 PDH / 2 years (GA)',   link: '/profession/professional-engineer' },
  { name: 'CPA',                    credit: 'CPE', example: '120 CPE / 3 years (NJ)',   link: '/profession/certified-public-accountant' },
  { name: 'Attorney',               credit: 'CLE', example: '24 CLE / 2 years (NY)',   link: '/profession/attorney' },
  { name: 'Registered Nurse',       credit: 'CE',  example: '24 CE / 2 years (FL)',    link: '/profession/registered-nurse' },
  { name: 'Mental Health Counselor',credit: 'CE',  example: '32 CE / 2 years (WA)',    link: '/profession/mental-health-counselor' },
  { name: 'Pharmacist',             credit: 'CE',  example: '45 CE / 3 years (NY)',    link: '/profession/pharmacist' },
  { name: 'Dentist',                credit: 'CE',  example: '60 CE / 3 years (NY)',    link: '/profession/dentist' },
  { name: 'Landscape Architect',    credit: 'PDH', example: '24 PDH / 2 years (WA)',   link: '/profession/landscape-architect' },
  { name: 'Social Worker',          credit: 'CE',  example: '36 CE / 2 years (CA)',    link: '/profession/social-worker' },
];

const FEATURES = [
  { icon: 'bi-list-check',      title: 'Track by mandatory category', body: 'State boards mandate specific topics (Ethics, HSW, Laws & Rules). CEC Track enforces minimums per category — not just total hours.' },
  { icon: 'bi-map',             title: 'Multi-state in one dashboard', body: 'Licensed in New York and Florida? Track both renewal cycles simultaneously. Each state\'s rules are loaded automatically.' },
  { icon: 'bi-bell',            title: 'Renewal deadline reminders',   body: 'Email alerts at 30, 14, 7, 3, and 1 day before your deadline. Annual, biennial, and triennial cycles all supported.' },
  { icon: 'bi-folder2-open',    title: 'Certificate storage',          body: 'Upload course completion certificates to each class record. Instantly accessible if your state board audits you.' },
  { icon: 'bi-graph-up-arrow',  title: 'Real-time compliance status',  body: 'Green/amber/red indicators show exactly where you stand at any moment — per category, per cycle, per license.' },
  { icon: 'bi-award',           title: '20+ professions supported',    body: 'Architects, engineers, CPAs, attorneys, nurses, pharmacists, counselors, social workers, dentists, and more.' },
];

const FAQ = [
  {
    q: 'What is CE tracking software?',
    a: 'CE tracking software helps licensed professionals record completed continuing education (CE) courses, track progress toward state licensing board requirements, and receive reminders before renewal deadlines. It eliminates manual spreadsheet tracking and reduces the risk of lapsing a professional license.',
  },
  {
    q: 'How is CEC Track different from CE Broker?',
    a: 'CE Broker is focused on healthcare professions and integrates directly with state licensing boards to auto-report completions. CEC Track is built for cross-profession use — architects, engineers, CPAs, attorneys, and healthcare professionals all in one platform — with manual certificate upload and multi-state tracking. CEC Track is free to start with no mandatory provider integration.',
  },
  {
    q: 'Can I track CE requirements across multiple states?',
    a: 'Yes. CEC Track supports 13 active states and shows the correct requirements for each state+profession combination. If you\'re a CPA licensed in New York and New Jersey, both sets of requirements appear on your dashboard simultaneously.',
  },
  {
    q: 'Does CEC Track track mandatory categories like Ethics or HSW?',
    a: 'Yes — this is a core differentiator. Many state boards require specific topic minimums (e.g., Florida PEs must complete 1 hour of Laws & Rules, 1 hour of Ethics, and 4 hours in their area of practice). CEC Track enforces these category-level requirements, not just total hour counts.',
  },
  {
    q: 'Is CEC Track free?',
    a: 'Free accounts can create one renewal cycle and track unlimited classes within it. Pro accounts support unlimited cycles, multiple state licenses, and team/firm dashboards. Try it free at cectrack.com.',
  },
  {
    q: 'What professions does CEC Track support?',
    a: 'Architects (LU/HSW), Professional Engineers (PDH), CPAs (CPE), Attorneys (CLE), Registered Nurses, Mental Health Counselors, Pharmacists, Dentists, Dental Hygienists, Landscape Architects, Social Workers, and more.',
  },
];

const STATES = [
  { label: 'New York',      href: '/ny-license-renewal' },
  { label: 'Texas',         href: '/tx-license-renewal' },
  { label: 'California',    href: '/ca-license-renewal' },
  { label: 'Florida',       href: '/fl-license-renewal' },
  { label: 'New Jersey',    href: '/nj-license-renewal' },
  { label: 'Connecticut',   href: '/ct-license-renewal' },
  { label: 'Illinois',      href: '/il-license-renewal' },
  { label: 'Pennsylvania',  href: '/pa-license-renewal' },
  { label: 'Georgia',       href: '/ga-license-renewal' },
  { label: 'Massachusetts', href: '/ma-license-renewal' },
  { label: 'Washington',    href: '/wa-license-renewal' },
];

const Eyebrow: React.FC<{ label: string; light?: boolean }> = ({ label, light }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
    <span style={{
      width: '8px', height: '8px', borderRadius: '50%',
      background: 'var(--color-accent)', flexShrink: 0, display: 'inline-block',
    }} />
    <span style={{
      fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)',
      fontWeight: 'var(--fw-semibold)',
      color: light ? 'rgba(255,255,255,0.55)' : 'var(--color-text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.12em',
    }}>
      {label}
    </span>
  </div>
);

const CETrackingGuide: React.FC = () => {
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'CEC Track',
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: 'Compliance Tracking Software',
      operatingSystem: 'Web Browser',
      url: 'https://cectrack.com',
      description: 'Continuing education compliance tracking software for licensed professionals. Tracks mandatory CE categories (Ethics, HSW, Laws & Rules) per state licensing board requirements across 20+ professions and 13+ states.',
      featureList: [
        'Track CE credits by mandatory category (Ethics, HSW, Laws & Rules, General)',
        'Multi-state license tracking on one dashboard',
        'Email renewal deadline reminders at 30, 14, 7, 3, and 1 day',
        'CE certificate storage for state board audits',
        'PDF compliance reports',
        'Team and firm compliance dashboards',
        'Supports 20+ professions including architects, engineers, CPAs, attorneys, nurses, social workers, pharmacists',
        'Biennial, triennial, and annual renewal cycle support',
      ],
      audience: {
        '@type': 'Audience',
        audienceType: 'Licensed professionals: architects, engineers, CPAs, attorneys, nurses, pharmacists, social workers, mental health counselors, dentists',
      },
      offers: [
        { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD', description: '1 renewal cycle, unlimited classes' },
        { '@type': 'Offer', name: 'Pro', price: '9', priceCurrency: 'USD', description: 'Unlimited cycles, multi-state, PDF reports, deadline alerts' },
        { '@type': 'Offer', name: 'Pro Annual', price: '7', priceCurrency: 'USD', description: 'Pro plan billed annually' },
      ],
      screenshot: 'https://cectrack.com/og-image.png',
      inLanguage: 'en-US',
      isAccessibleForFree: true,
      softwareVersion: '2025',
    },
  ];

  return (
    <>
      <SEOHelmet
        title="CE Tracking Software — Track CE Credits for Every Profession & State"
        description="CEC Track is free CE tracking software for architects, engineers, CPAs, attorneys, nurses, and 20+ other licensed professions. Track mandatory categories, renewal deadlines, and compliance across multiple states."
        keywords="ce tracking software, ce credit tracker, continuing education tracking, professional license CE tracker, multi-state CE tracking, PDH tracker, CPE tracker, CLE tracker, LU tracker, nurse CE tracking"
        canonical="https://cectrack.com/ce-tracking-software"
        structuredData={structuredData}
      />

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section style={{
        background: 'var(--color-surface-paper)',
        borderBottom: '1px solid var(--color-border)',
        padding: '5rem 0 4.5rem',
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'center',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(15,31,61,0.05) 1px, transparent 0)',
          backgroundSize: '28px 28px',
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: '860px', margin: '0 auto', padding: '0 2rem', position: 'relative' }}>
          <Eyebrow label="CE Tracking Software" />

          <h1 style={{
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: 'clamp(2.75rem, 6.5vw, 6rem)',
            lineHeight: 1.0, letterSpacing: '-0.03em',
            color: 'var(--color-primary)', marginBottom: '1.75rem',
          }}>
            Track CE Credits<br />
            for Every Profession<br />
            <em style={{ color: 'var(--color-accent)', fontStyle: 'italic' }}>&amp; State.</em>
          </h1>

          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)',
            lineHeight: 1.75, color: 'var(--color-text-muted)',
            maxWidth: '600px', margin: '0 auto 2.5rem',
          }}>
            CEC Track is free CE tracking software built for licensed professionals —
            architects, engineers, CPAs, attorneys, nurses, and 20+ more. Mandatory
            categories, multi-state cycles, and renewal deadlines all in one dashboard.
          </p>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '3.5rem' }}>
            <Link
              to="/register"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.875rem 2rem',
                background: 'var(--color-primary)', color: '#ffffff',
                fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)',
                fontSize: 'var(--fs-base)', borderRadius: 'var(--radius-md)',
                textDecoration: 'none',
                transition: 'background var(--transition-base), transform var(--transition-base)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-primary-hover)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Start Tracking Free
              <i className="bi bi-arrow-right" />
            </Link>
            <Link
              to="/how-it-works"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.875rem 2rem',
                background: 'transparent', color: 'var(--color-text)',
                fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-medium)',
                fontSize: 'var(--fs-base)', borderRadius: 'var(--radius-md)',
                textDecoration: 'none', border: '1px solid var(--color-border-strong)',
                transition: 'border-color var(--transition-base)',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-text)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; }}
            >
              How It Works
            </Link>
          </div>

          {/* Stats strip */}
          <div style={{
            display: 'flex', gap: '3rem', flexWrap: 'wrap', justifyContent: 'center',
            paddingTop: '2rem', borderTop: '1px solid var(--color-border)',
          }}>
            {[
              { n: '20+',  unit: 'professions',    sub: 'and growing'       },
              { n: '13',   unit: 'active states',  sub: 'fully verified'    },
              { n: '100%', unit: 'free to start',  sub: 'no credit card'    },
              { n: '6+',   unit: 'category types', sub: 'per state tracked' },
            ].map(({ n, unit, sub }) => (
              <div key={unit} style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', alignItems: 'center' }}>
                <span style={{
                  fontFamily: 'var(--font-display)', fontWeight: 700,
                  fontSize: 'var(--fs-2xl)', color: 'var(--color-primary)', lineHeight: 1,
                }}>{n}</span>
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)',
                  color: 'var(--color-accent)', fontWeight: 'var(--fw-semibold)',
                  letterSpacing: '0.05em', textTransform: 'uppercase',
                }}>{unit}</span>
                <span style={{
                  fontFamily: 'var(--font-body)', fontSize: '0.7rem',
                  color: 'var(--color-text-muted)',
                }}>{sub}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What Is CE Tracking ───────────────────────────────────────────────── */}
      <section style={{ background: 'var(--color-surface-raised)', padding: '5rem 0' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 2rem' }}>
          <Eyebrow label="The Basics" />
          <h2 style={{
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1.1, letterSpacing: '-0.02em',
            color: 'var(--color-primary)', marginBottom: '1.5rem',
          }}>
            What Is CE Tracking Software?
          </h2>
          <div style={{ maxWidth: '700px' }}>
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)',
              color: 'var(--color-text-muted)', lineHeight: 1.8, marginBottom: '1rem',
            }}>
              CE tracking software helps licensed professionals record completed continuing
              education courses, monitor progress toward state licensing board requirements,
              and stay ahead of renewal deadlines — without spreadsheets.
            </p>
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)',
              color: 'var(--color-text-muted)', lineHeight: 1.8,
            }}>
              Every licensed profession has different rules: how many hours per renewal
              cycle, which topics are mandatory, how many hours must be live vs. self-study,
              and whether credits carry over between cycles. CE tracking software like CEC
              Track enforces these rules automatically so you always know exactly where
              you stand.
            </p>
          </div>
        </div>
      </section>

      {/* ── Features Grid ────────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--color-surface-muted)', padding: '5rem 0' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 2rem' }}>
          <Eyebrow label="Platform Features" />
          <h2 style={{
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1.1, letterSpacing: '-0.02em',
            color: 'var(--color-primary)', marginBottom: '0.75rem',
          }}>
            How CEC Track Works
          </h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)',
            color: 'var(--color-text-muted)', marginBottom: '3rem',
          }}>
            Add your licenses, log your classes, stay compliant — across every state and profession.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '1.5rem' }}>
            {FEATURES.map((f, i) => (
              <div
                key={i}
                style={{
                  background: 'var(--color-surface-raised)',
                  borderTop: '1px solid var(--color-border)',
                  borderRight: '1px solid var(--color-border)',
                  borderBottom: '1px solid var(--color-border)',
                  borderLeft: '4px solid var(--color-accent)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1.5rem 1.75rem',
                  boxShadow: 'var(--shadow-xs)',
                }}
              >
                <i
                  className={`bi ${f.icon}`}
                  style={{
                    fontSize: '1.5rem',
                    color: 'var(--color-accent)',
                    display: 'block',
                    marginBottom: '0.875rem',
                  }}
                />
                <h3 style={{
                  fontFamily: 'var(--font-display)', fontWeight: 700,
                  fontSize: 'var(--fs-lg)', color: 'var(--color-primary)',
                  marginBottom: '0.5rem', lineHeight: 1.25,
                }}>
                  {f.title}
                </h3>
                <p style={{
                  fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)',
                  color: 'var(--color-text-muted)', lineHeight: 1.65, margin: 0,
                }}>
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Professions Table ─────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--color-surface-raised)', padding: '5rem 0' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 2rem' }}>
          <Eyebrow label="Supported Professions" />
          <h2 style={{
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1.1, letterSpacing: '-0.02em',
            color: 'var(--color-primary)', marginBottom: '0.75rem',
          }}>
            Every Licensed Profession
          </h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)',
            color: 'var(--color-text-muted)', marginBottom: '2.5rem',
          }}>
            State-specific CE requirements for 20+ licensed professions, verified from official state licensing boards.
          </p>

          <div style={{ overflowX: 'auto', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-card)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
              <thead>
                <tr style={{ background: 'var(--color-primary)' }}>
                  <th style={{
                    padding: '0.875rem 1.25rem', textAlign: 'left',
                    fontFamily: 'var(--font-mono)', fontWeight: 'var(--fw-semibold)',
                    fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.65)',
                    textTransform: 'uppercase', letterSpacing: '0.1em',
                  }}>Profession</th>
                  <th style={{
                    padding: '0.875rem 1.25rem', textAlign: 'left',
                    fontFamily: 'var(--font-mono)', fontWeight: 'var(--fw-semibold)',
                    fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.65)',
                    textTransform: 'uppercase', letterSpacing: '0.1em',
                  }}>Credit Type</th>
                  <th style={{
                    padding: '0.875rem 1.25rem', textAlign: 'left',
                    fontFamily: 'var(--font-mono)', fontWeight: 'var(--fw-semibold)',
                    fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.65)',
                    textTransform: 'uppercase', letterSpacing: '0.1em',
                  }}>Example Requirement</th>
                  <th style={{ padding: '0.875rem 1.25rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {PROFESSIONS.map((p, i) => (
                  <tr
                    key={i}
                    style={{
                      borderBottom: '1px solid var(--color-border)',
                      background: i % 2 === 0 ? 'var(--color-surface-raised)' : 'var(--color-surface-paper)',
                    }}
                  >
                    <td style={{
                      padding: '0.875rem 1.25rem',
                      fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)',
                      color: 'var(--color-primary)',
                    }}>{p.name}</td>
                    <td style={{ padding: '0.875rem 1.25rem' }}>
                      <code style={{
                        background: 'var(--color-accent-light)',
                        color: 'var(--color-primary)',
                        padding: '0.15rem 0.5rem',
                        borderRadius: 'var(--radius-sm)',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--fs-xs)',
                        fontWeight: 'var(--fw-semibold)',
                        letterSpacing: '0.04em',
                      }}>{p.credit}</code>
                    </td>
                    <td style={{
                      padding: '0.875rem 1.25rem',
                      fontFamily: 'var(--font-body)',
                      color: 'var(--color-text-muted)',
                    }}>{p.example}</td>
                    <td style={{ padding: '0.875rem 1.25rem' }}>
                      <Link
                        to={p.link}
                        style={{
                          fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)',
                          fontSize: 'var(--fs-xs)', color: 'var(--color-accent)',
                          textDecoration: 'none', letterSpacing: '0.02em',
                        }}
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── State Coverage ────────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--color-surface-paper)', padding: '5rem 0' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 2rem' }}>
          <Eyebrow label="State Coverage" />
          <h2 style={{
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1.1, letterSpacing: '-0.02em',
            color: 'var(--color-primary)', marginBottom: '0.75rem',
          }}>
            CE Requirements by State
          </h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)',
            color: 'var(--color-text-muted)', marginBottom: '2.5rem',
          }}>
            Detailed guides to CE requirements for every profession in 13 states — sourced directly from official licensing boards.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            {STATES.map(s => (
              <Link
                key={s.href}
                to={s.href}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  background: 'var(--color-surface-raised)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '0.625rem 1.125rem',
                  fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)',
                  fontSize: 'var(--fs-sm)', color: 'var(--color-primary)',
                  textDecoration: 'none',
                  transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-accent)'; e.currentTarget.style.boxShadow = 'var(--shadow-xs)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                {s.label}
                <i className="bi bi-arrow-right" style={{ fontSize: '0.75rem', color: 'var(--color-accent)' }} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--color-surface-raised)', padding: '5rem 0' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 2rem' }}>
          <Eyebrow label="Common Questions" />
          <h2 style={{
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1.1, letterSpacing: '-0.02em',
            color: 'var(--color-primary)', marginBottom: '3rem',
          }}>
            Frequently Asked Questions
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', maxWidth: '740px' }}>
            {FAQ.map((item, i) => (
              <div
                key={i}
                style={{
                  borderLeft: '3px solid var(--color-accent)',
                  paddingLeft: '1.5rem',
                }}
              >
                <h3 style={{
                  fontFamily: 'var(--font-display)', fontWeight: 700,
                  fontSize: 'var(--fs-lg)', color: 'var(--color-primary)',
                  marginBottom: '0.625rem', lineHeight: 1.3,
                }}>
                  {item.q}
                </h3>
                <p style={{
                  fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)',
                  color: 'var(--color-text-muted)', lineHeight: 1.75, margin: 0,
                }}>
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────────── */}
      <section style={{
        background: 'var(--color-primary)',
        padding: '6rem 2rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(42,191,191,0.07) 1px, transparent 0)',
          backgroundSize: '28px 28px',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', maxWidth: '640px', margin: '0 auto' }}>
          <Eyebrow label="Get Started" light />

          <h2 style={{
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: 'clamp(2.25rem, 5vw, 4.5rem)',
            lineHeight: 1.05, letterSpacing: '-0.03em',
            color: '#ffffff', marginBottom: '1.25rem',
          }}>
            Stop Tracking CE Credits<br />
            <em style={{ color: 'var(--color-accent)', fontStyle: 'italic' }}>Manually.</em>
          </h2>

          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)',
            color: 'rgba(255,255,255,0.65)', lineHeight: 1.75,
            marginBottom: '2.5rem', maxWidth: '480px', margin: '0 auto 2.5rem',
          }}>
            Join licensed professionals who use CEC Track to stay compliant without
            spreadsheets or last-minute scrambles.
          </p>

          <Link
            to="/register"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '1rem 2.5rem',
              background: 'var(--color-accent)', color: 'var(--color-primary)',
              fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-bold)',
              fontSize: 'var(--fs-base)', borderRadius: 'var(--radius-md)',
              textDecoration: 'none',
              transition: 'background var(--transition-base), transform var(--transition-base)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-accent-hover)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-accent)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Start Free — No Credit Card Required
            <i className="bi bi-arrow-right" />
          </Link>

          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)',
            color: 'rgba(255,255,255,0.35)', marginTop: '1.25rem',
            letterSpacing: '0.05em', textTransform: 'uppercase',
          }}>
            Free forever · Upgrade anytime · No credit card
          </p>
        </div>
      </section>
    </>
  );
};

export default CETrackingGuide;
